import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

// ── Types ────────────────────────────────────────────────────────────────────

export type ToastVariant = "error" | "success" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
  error: (message: string) => void;
  success: (message: string) => void;
}

// ── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

const DURATION_MS = 4500;

const VARIANT_STYLES: Record<ToastVariant, {
  bar: string;
  border: string;
  icon: JSX.Element;
  label: string;
  surface: string;
}> = {
  error: {
    bar: "bg-[var(--status-rejected-text)]",
    border: "border-status-rejected-border",
    label: "text-status-rejected-text",
    surface: "bg-status-rejected-bg",
    icon: (
      <svg className="h-5 w-5 shrink-0 text-status-rejected-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
  success: {
    bar: "bg-[var(--status-active-text)]",
    border: "border-status-active-border",
    label: "text-status-active-text",
    surface: "bg-status-active-bg",
    icon: (
      <svg className="h-5 w-5 shrink-0 text-status-active-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  warning: {
    bar: "bg-[var(--status-nigo-text)]",
    border: "border-status-nigo-border",
    label: "text-status-nigo-text",
    surface: "bg-status-nigo-bg",
    icon: (
      <svg className="h-5 w-5 shrink-0 text-status-nigo-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
  info: {
    bar: "bg-accent-strong",
    border: "border-accent-muted",
    label: "text-content-primary",
    surface: "bg-accent-muted",
    icon: (
      <svg className="h-5 w-5 shrink-0 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
      </svg>
    ),
  },
};

// ── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
  }, []);

  const toast = useCallback((message: string, variant: ToastVariant = "info") => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev.slice(-4), { id, message, variant }]);
    const timer = setTimeout(() => dismiss(id), DURATION_MS);
    timers.current.set(id, timer);
  }, [dismiss]);

  const error = useCallback((msg: string) => toast(msg, "error"), [toast]);
  const success = useCallback((msg: string) => toast(msg, "success"), [toast]);

  return (
    <ToastContext.Provider value={{ toast, error, success }}>
      {children}

      {/* ── Toast container ─ fixed bottom-right ── */}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 items-end pointer-events-none">
        <AnimatePresence initial={false}>
          {toasts.map((t) => {
            const { bar, border, icon, label, surface } = VARIANT_STYLES[t.variant];
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.95 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                role={t.variant === "error" ? "alert" : "status"}
                aria-live={t.variant === "error" ? "assertive" : "polite"}
                className={`pointer-events-auto w-[340px] max-w-[calc(100vw-2.5rem)]
                           overflow-hidden rounded-xl border shadow-card-hover ${border} ${surface}`}
              >
                <div className={`h-0.5 w-full ${bar}`} />
                <div className="flex items-start gap-3 px-4 py-3">
                  <span className="mt-0.5">{icon}</span>
                  <p className={`flex-1 text-sm font-medium leading-snug ${label}`}>
                    {t.message}
                  </p>
                  <button
                    type="button"
                    onClick={() => dismiss(t.id)}
                    className="focus-ring -mr-2 -mt-2 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-raised/60 hover:text-content-primary"
                    aria-label={`Dismiss ${t.variant} notification`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
